import React from 'react';
import PropTypes from 'prop-types';
import BalanceSheetTableContainer from 'Components/UI/BalanceSheetTable/Container';
import InvestmentKnowledge
  from 'Components/ClientApp/CombinedApp/Viewport/Dashboard/GoalsAndObjectives/TimeHorizonContainer/InvestmentKnowledge';
import ShadowBox from 'Components/UI/ShadowBox';
import EditButtonLink from 'Components/Blueprint/UI/EditButtonLink';
import { useBlueprintContext } from 'Context/BlueprintContext';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import { editQuestionnaire } from 'Consts/Questionnaire';
import CashFlow from '../../UI/CashFlow';
import Content from '../../UI/Content';
import '../../UI/FinancialSummary/index.css';
import Income from './Income';
import Taxes from './Taxes';

const reduceIncomeBySection = (acc, cur) => {
  if (!(cur.section in acc)) {
    acc[cur.section] = [{ name: cur.name, value: cur.value }];
  } else {
    acc[cur.section].push({ name: cur.name, value: cur.value });
  }
  return acc;
};

const transformIncomeData = (data) => {
  const dataMapped = data.map(d => d.attributes);
  const dataPrimaryObj = dataMapped
    .filter(d => d.index === 0)
    .reduce(reduceIncomeBySection, {});
  const dataPrimaryKeys = Object.keys(dataPrimaryObj);
  const dataPrimaryArray = dataPrimaryKeys.map(k => ({
    name: k,
    value: dataPrimaryObj[k],
  }));
  const dataSecondaryObj = dataMapped
    .filter(d => d.index === 1)
    .reduce(reduceIncomeBySection, {});
  const dataSecondaryKeys = Object.keys(dataSecondaryObj);
  const dataSecondaryArray = dataSecondaryKeys.map(k => ({
    name: k,
    value: dataSecondaryObj[k],
  }));
  return {
    primary: dataPrimaryArray,
    secondary: dataSecondaryArray,
  };
};

const FinancialSummary = ({
  income,
  cashFlow,
  investmentProfileAndKnowledge,
  isAgent,
}) => {
  const { isReadOnly } = useBlueprintContext();
  const transformedIncome = transformIncomeData(income);
  const primaryIncome = transformedIncome ? transformedIncome.primary : [];
  const spouseIncome = transformedIncome ? transformedIncome.secondary : [];
  const incomeTotal = income
    .map(d => d.attributes)
    .reduce((acc, cur) => acc + cur.value, 0);
  const simpleHousehold = useSimpleHousehold();

  const {
    stocks,
    bonds,
    mutualFunds,
    options,
    variableAnnuities,
    variableLife,
    alternatives,
  } = investmentProfileAndKnowledge.attributes;
  const knowledge = [
    { name: 'Stocks', value: stocks },
    { name: 'Bonds', value: bonds },
    { name: 'Mutual Funds', value: mutualFunds },
    { name: 'Options', value: options },
    { name: 'Variable Annuities', value: variableAnnuities },
    { name: 'Variable Life', value: variableLife },
    { name: 'Alternatives', value: alternatives },
  ];

  function getClientNamePrimary() {
    if (!simpleHousehold || !simpleHousehold.householdResource) {
      return '';
    }
    return `${simpleHousehold.householdResource.attr('primaryClientFirstName')} ${simpleHousehold.householdResource.attr('primaryClientLastName')}`;
  }
  function getClientNameSecondary() {
    if (!simpleHousehold || !simpleHousehold.householdResource) {
      return '';
    }
    return `${simpleHousehold.householdResource.attr('spouseClientFirstName')} ${simpleHousehold.householdResource.attr('spouseClientLastName')}`;
  }

  function getEditLink(page, section) {
    if (simpleHousehold.householdResource) {
      return editQuestionnaire(page, {
        section,
        isAgent,
        householdId: simpleHousehold.householdResource.id(),
      });
    }
    return null;
  }

  return (
    <Content>
      <div className="blueprint-financial-summary-container">
        <div className="income-etc">
          <ShadowBox className="income-container">
            <h4 className="bordered">Income</h4>
            <div className="income-container-inner">
              <Income
                income={income}
                incomeTitle="Primary"
                incomeData={primaryIncome}
                clientName={getClientNamePrimary()}
                editLink={getEditLink('incomeDetails', 'incomeDetails')}
              />
              {spouseIncome.length ? (
                <Income
                  incomeTitle="Secondary"
                  incomeData={spouseIncome}
                  clientName={getClientNameSecondary()}
                  editLink={getEditLink('incomeDetails', 'secondaryIncomeDetails')}
                />
              ) : (
                ''
              )}
            </div>
          </ShadowBox>
          <ShadowBox className="cashflow-container">
            <h4 className="bordered">Cash Flow</h4>
            {cashFlow.length > 0 ? (
              <CashFlow
                cashFlow={cashFlow[0].attributes}
                grossTotalIncome={incomeTotal}
                householdId={investmentProfileAndKnowledge.id}
                isAgent={isAgent}
              />
            ) : null}
          </ShadowBox>
          <ShadowBox className="taxes-container">
            <h4 className="bordered">
                Taxes
              {getEditLink('incomeDetails', 'incomeTaxSummary') && (<EditButtonLink
                title="Edit Taxes"
                to={getEditLink('incomeDetails', 'incomeTaxSummary')}
              />)}
            </h4>
            <Taxes />
          </ShadowBox>
        </div>
        <div className="balance-sheet-etc">
          <ShadowBox>
            <h4 className="bordered">Balance Sheet</h4>
            <div className="balance-sheet-networth-container">
              <div className="balance-sheet-container">
                <BalanceSheetTableContainer
                  showTitle={false}
                  isReadOnly={isReadOnly}
                />
              </div>
            </div>
          </ShadowBox>
        </div>
        <ShadowBox className="time-horizon-etc">
          <div className="time-horizon-knowledge-container">
            <div className="knowledge-container">
              <h4>
                Investment Knowledge
                {getEditLink('financialProfile', 'investmentKnowledge') && (<EditButtonLink
                  title="Edit Investment Knowledge"
                  to={getEditLink('financialProfile', 'investmentKnowledge')}
                />)}
              </h4>
              <InvestmentKnowledge knowledge={knowledge} />
            </div>
          </div>
        </ShadowBox>
      </div>
    </Content>
  );
};

FinancialSummary.propTypes = {
  income: PropTypes.array.isRequired,
  cashFlow: PropTypes.array.isRequired,
  investmentProfileAndKnowledge: PropTypes.object.isRequired,
  isAgent: PropTypes.bool.isRequired,
};

export default FinancialSummary;
